import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Pagination, type GridColDef } from '@dhl/components'
import { Box, Button, Grid, IconButton, Paper } from '@dhl-ui/components'
import { EditSolidIcon, PlusIcon } from '@dhl-ui/icons'
import { type IntegratorDto, useGetIntegrators } from '@dhl/mdp-api'

import { links } from '~/AppRoutes'
import { AdminDataGrid, lowercaseTransform, SearchTextField, useSearchAndPage } from '~/common'

export const IntegratorsPage = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { searchAndPageParams, handleSearch, page, pageSize, setPage, setPageSize } = useSearchAndPage()

  const { data, isLoading } = useGetIntegrators(searchAndPageParams)

  const handleRowClick = (row: IntegratorDto) => {
    navigate(links.integratorDetailsLink(row.id || ''))
  }

  const renderString = (value: string | undefined) => {
    return value ? value : ''
  }

  const renderEdit = (row: IntegratorDto) => {
    return (
      <IconButton
        aria-label="edit integrator details"
        onClick={() => navigate(links.editIntegratorDetailsLink(row.id || ''))}
      >
        <EditSolidIcon data-testid="edit-integrators-details" />
      </IconButton>
    )
  }

  const columns: GridColDef<IntegratorDto>[] = [
    {
      field: 'name',
      headerName: t('admin.integratorsPage.name'),
      flex: 1,
      valueFormatter: renderString,
    },
    {
      field: 'description',
      headerName: t('admin.integratorsPage.description'),
      flex: 1,
      valueFormatter: renderString,
    },
    {
      field: 'limitedTrackTrace',
      headerName: t('admin.integratorsPage.limited'),
      flex: 1,
    },
    {
      field: 'actions',
      headerName: t('admin.integratorsPage.actions'),
      flex: 1,
      renderCell: ({ row }) => renderEdit(row),
    },
  ]

  return (
    <>
      <Grid container direction="row" justifyContent="space-between" alignItems="center" data-testid="integrators-page">
        <Grid sx={{ backgroundColor: 'common.white' }}>
          <SearchTextField onChange={handleSearch} transform={lowercaseTransform} />
        </Grid>
        <Grid>
          <Button
            data-testid="add-integrators"
            color="primary"
            variant="contained"
            startIcon={<PlusIcon />}
            onClick={() => navigate(links.integratorLink())}
          >
            {t('admin.integratorsPage.addIntegrators')}
          </Button>
        </Grid>
      </Grid>
      <Box mt={2}>
        <Paper>
          <AdminDataGrid
            loading={isLoading}
            data-testid="integrators-table"
            autoHeight
            rows={data?.content ?? []}
            columns={columns}
            onRowClick={({ row }) => handleRowClick(row)}
            rowHeight={48}
            initialState={{
              sorting: {
                sortModel: [{ field: 'name', sort: 'asc' }],
              },
            }}
            columnHeaderHeight={48}
            disableRowSelectionOnClick
            disableColumnFilter
            hideFooter
          />
        </Paper>
        <Box mt={2}>
          <Pagination
            page={page}
            count={data?.totalCount ?? 0}
            onPageChange={(_, page) => {
              setPage(page)
            }}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(pageSize) => {
              setPageSize(pageSize)
            }}
          />
        </Box>
      </Box>
    </>
  )
}
